/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { EntitySwagger, LabelsPerEntitySwagger } from 'src/luis/projects/entities/types/swagger/EntitySwagger';

/**
 * Represents a service to perform common operations for entity.
 */
export const getEntityService = createService(({ context, getEnvironment }) => {
    const { webApiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url scheme for this service.
     *
     * @returns A url string to call.
     */
    const getUrl = (): string =>
        urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('detailedModels')
            .queryParam('modelFilterType', 'Entity')
            .queryParam('take', '1000')
            .build();

    /**
     * Gets a map of entity ids and their respective
     * labels count.
     */
    const getLabelsPerEntityCount = async (): Promise<LabelsPerEntitySwagger> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('stats')
            .resource('labelsPerEntity')
            .build();

        const { data } = await httpService.get<LabelsPerEntitySwagger>(url, apiOptions);

        return data;
    };

    /**
     * Gets the list of entities that belong to provided application and version.
     *
     */
    const getEntities = async (): Promise<{ entities: readonly EntitySwagger[]; labelsPerEntity: LabelsPerEntitySwagger }> => {
        const fetchIntents = httpService.get<readonly EntitySwagger[]>(getUrl(), apiOptions);
        const fetchLabelsPerEntityCount = getLabelsPerEntityCount();

        const [{ data: entities }, labelsPerEntity] = await Promise.all([fetchIntents, fetchLabelsPerEntityCount]);

        return { entities, labelsPerEntity };
    };

    return { getEntities };
});
